import { Button, Select, TextInput } from "@hisani/ui";
import {
  Bookmark,
  Check,
  ChevronDown,
  Clipboard,
  Download,
  Heart,
  MapPin,
  Search,
} from "lucide-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import React, { useEffect, useRef, type ReactNode } from "react";
import type {
  PlannerLocale,
  PlannerRace,
  PlannerSort,
} from "./planner-contracts";
import {
  plannerBreedLabel,
  plannerCompactPurseAmountLabel,
  plannerCurrencySymbol,
  plannerSurfaceLabel,
} from "./planner-formatting";
import {
  PlannerEmptyState,
  PlannerErrorState,
  PlannerLoadingState,
} from "./planner-result-states";

export function PlannerResults({
  races,
  locale,
  savedView,
  authLoading,
  exporting,
  error,
  actionError,
  loading,
  loadingMore,
  copiedRaceId,
  totalCount,
  hasMore,
  hasActiveFilters,
  activeFilters,
  search,
  sort,
  mobileFilters,
  onShowResults,
  onShowSaved,
  onSearchChange,
  onSortChange,
  onExport,
  onRetry,
  onCopy,
  onSave,
  onLoadMore,
}: {
  readonly races: readonly PlannerRace[];
  readonly locale: PlannerLocale;
  readonly savedView: boolean;
  readonly authLoading: boolean;
  readonly exporting: boolean;
  readonly error: string | null;
  readonly actionError: string | null;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly copiedRaceId: string | null;
  readonly totalCount: number;
  readonly hasMore: boolean;
  readonly hasActiveFilters: boolean;
  readonly activeFilters: ReactNode;
  readonly search: string;
  readonly sort: PlannerSort;
  readonly mobileFilters: ReactNode;
  readonly onShowResults: () => void;
  readonly onShowSaved: () => void;
  readonly onSearchChange: (search: string) => void;
  readonly onSortChange: (sort: PlannerSort) => void;
  readonly onExport: () => void;
  readonly onRetry: () => void;
  readonly onCopy: (race: PlannerRace) => void;
  readonly onSave: (race: PlannerRace) => void;
  readonly onLoadMore: () => void;
}) {
  const t = useTranslations("Planner");
  return (
    <section
      className="min-w-0"
      aria-busy={loading}
      aria-labelledby="planner-results-title"
    >
      <h1 id="planner-results-title" className="sr-only">
        {t("title")}
      </h1>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 z-10 size-5 -translate-y-1/2 text-secondary"
            aria-hidden="true"
          />
          <TextInput
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label={t("nameSearchLabel")}
            placeholder={t("nameSearchPlaceholder")}
            className="min-h-11 rounded-xl ps-10"
          />
        </div>
        {mobileFilters}
      </div>
      <div
        className={`mt-2 flex min-h-11 items-center gap-2 rounded-lg bg-brand-subtle px-2 ${hasActiveFilters ? "justify-between" : "justify-center"}`}
      >
        {activeFilters}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 shrink-0 gap-2 px-2 text-brand-strong hover:bg-transparent active:translate-y-0 active:bg-transparent"
          onClick={savedView ? onShowResults : onShowSaved}
          disabled={authLoading}
          aria-pressed={savedView}
        >
          <Bookmark
            className={savedView ? "size-4 fill-current" : "size-4"}
            aria-hidden="true"
          />
          {savedView ? t("publishedRaces") : t("savedRaces")}
        </Button>
      </div>

      {error !== null && races.length === 0 ? (
        <PlannerErrorState error={error} onRetry={onRetry} />
      ) : (
        <>
          <div className="mt-2 flex min-h-11 flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-base font-bold text-primary">
                {t("resultCount", { count: totalCount })}
              </p>
              {loading && races.length > 0 ? (
                <p className="text-xs leading-4 text-secondary" role="status">
                  {t("refreshing")}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <span className="shrink-0 text-sm font-semibold text-secondary">
                {t("sortLabel")}
              </span>
              <Select
                value={sort}
                onValueChange={(value) =>
                  onSortChange(value === "OLDEST" ? "OLDEST" : "NEWEST")
                }
                aria-label={t("sortLabel")}
                variant="seamless"
                className="w-auto"
              >
                <option value="NEWEST">{t("sortNewest")}</option>
                <option value="OLDEST">{t("sortOldest")}</option>
              </Select>
              {races.length === 0 ? null : (
                <Button
                  type="button"
                  aria-label={exporting ? t("exporting") : t("exportResults")}
                  loading={exporting}
                  onClick={onExport}
                  variant="ghost"
                  size="sm"
                  className="min-h-11 shrink-0 gap-2 px-2"
                >
                  {exporting ? null : (
                    <Download className="size-4" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">
                    {exporting ? t("exporting") : t("exportResults")}
                  </span>
                </Button>
              )}
            </div>
          </div>

          {actionError === null ? null : (
            <p
              className="mt-3 border-y border-border-subtle py-3 text-sm font-semibold text-primary"
              role="alert"
            >
              {actionError}
            </p>
          )}
          {error === null || races.length === 0 ? null : (
            <PlannerErrorState error={error} onRetry={onRetry} compact />
          )}

          {loading && races.length === 0 ? (
            <PlannerLoadingState />
          ) : races.length === 0 ? (
            <PlannerEmptyState savedView={savedView} />
          ) : (
            <div className="mt-2 divide-y divide-border-subtle border-y border-border-subtle bg-surface">
              {races.map((race) => (
                <RaceCard
                  key={race.id}
                  race={race}
                  locale={locale}
                  copied={copiedRaceId === race.id}
                  authLoading={authLoading}
                  onCopy={() => onCopy(race)}
                  onSave={() => onSave(race)}
                />
              ))}
            </div>
          )}
          <AutomaticPageLoader
            hasMore={hasMore}
            loading={loadingMore}
            onLoadMore={onLoadMore}
          />
          {races.length === 0 ? null : (
            <p className="mt-4 border-t border-border-subtle pt-4 text-xs leading-5 text-secondary [word-spacing:var(--word-spacing-body)]">
              {t("disclaimer")}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function AutomaticPageLoader({
  hasMore,
  loading,
  onLoadMore,
}: {
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly onLoadMore: () => void;
}) {
  const t = useTranslations("Planner");
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (sentinel === null || !hasMore || loading) return;
    let requested = false;
    const requestNextPage = () => {
      if (requested) return;
      requested = true;
      onLoadMore();
    };

    if (typeof window.IntersectionObserver === "function") {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some(({ isIntersecting }) => isIntersecting)) {
            observer.disconnect();
            requestNextPage();
          }
        },
        { rootMargin: "240px 0px" },
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }

    const loadWhenNearViewport = () => {
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + 240) {
        window.removeEventListener("scroll", loadWhenNearViewport);
        window.removeEventListener("resize", loadWhenNearViewport);
        requestNextPage();
      }
    };
    window.addEventListener("scroll", loadWhenNearViewport, { passive: true });
    window.addEventListener("resize", loadWhenNearViewport);
    loadWhenNearViewport();
    return () => {
      window.removeEventListener("scroll", loadWhenNearViewport);
      window.removeEventListener("resize", loadWhenNearViewport);
    };
  }, [hasMore, loading, onLoadMore]);

  if (!hasMore && !loading) return null;
  return (
    <div
      ref={sentinelRef}
      className="flex min-h-8 items-center justify-center"
      role={loading ? "status" : undefined}
    >
      {loading ? (
        <span className="text-xs font-semibold text-secondary">
          {t("loadingMore")}
        </span>
      ) : null}
    </div>
  );
}

function RaceCard({
  race,
  locale,
  copied,
  authLoading,
  onCopy,
  onSave,
}: {
  readonly race: PlannerRace;
  readonly locale: PlannerLocale;
  readonly copied: boolean;
  readonly authLoading: boolean;
  readonly onCopy: () => void;
  readonly onSave: () => void;
}) {
  const t = useTranslations("Planner");
  const surface = plannerSurfaceLabel(race.surface, t);
  const breed = plannerBreedLabel(race.breed, t);
  const date = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${race.raceDate}T12:00:00Z`));
  const purseAmount = plannerCompactPurseAmountLabel(race, locale);
  const title =
    race.raceName ?? race.grade ?? race.category ?? t("notAvailable");
  return (
    <article className="grid bg-surface px-2 py-2 transition-colors duration-[var(--motion-duration-fast)] ease-[var(--motion-easing-standard)] hover:bg-subtle motion-reduce:transition-none sm:px-3">
      <details className="group contents">
        <summary className="relative col-start-1 row-start-1 cursor-pointer list-none pe-[132px] marker:hidden [&::-webkit-details-marker]:hidden">
          <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-[12px] leading-[16px]">
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 font-bold ${raceSurfaceClassName(race.surface)}`}
            >
              {surface}
            </span>
            <span className="min-w-0 font-semibold text-primary">{breed}</span>
            <span className="ms-1 flex max-w-full shrink-0 items-center gap-1 font-semibold text-primary">
              <span>
                {race.distanceMeters === null
                  ? t("notAvailable")
                  : `${race.distanceMeters.toLocaleString(locale)} ${t("metres")}`}
              </span>
              {race.currency === "AED" ? (
                <Image
                  src="/icons/aed-symbol.svg"
                  alt={t("aedCurrency")}
                  width={14}
                  height={12}
                  loading="eager"
                  className="shrink-0"
                />
              ) : (
                <span>{plannerCurrencySymbol(race.currency, locale)}</span>
              )}
              <span>{purseAmount}</span>
            </span>
          </div>
          <h2 className="mt-1 mb-1 whitespace-normal [overflow-wrap:anywhere] text-[16px] leading-[20px] font-bold tracking-tight text-primary">
            {title}
          </h2>
          <p className="flex min-w-0 items-center gap-1 text-[12px] leading-[16px] text-secondary">
            <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {locale === "ar" ? race.racecourse.nameAr : race.racecourse.name}
            </span>
            <span aria-hidden="true">·</span>
            <time className="shrink-0 font-semibold">{date}</time>
          </p>
          <ChevronDown
            className="pointer-events-none absolute end-0 top-1/2 size-4 -translate-y-1/2 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <RaceDetails race={race} locale={locale} />
      </details>
      <div className="pointer-events-none z-10 col-start-1 row-start-1 me-6 flex self-center justify-self-end gap-1">
        <Button
          type="button"
          onClick={onSave}
          aria-pressed={race.saved}
          aria-label={race.saved ? t("unsave") : t("save")}
          disabled={authLoading}
          variant="ghost"
          size="sm"
          className="pointer-events-auto size-[44px] rounded-md px-0 hover:bg-transparent active:translate-y-0 active:bg-transparent"
        >
          <Heart
            className={`size-[16px] ${race.saved ? "fill-accent text-accent" : ""}`}
            aria-hidden="true"
          />
        </Button>
        <Button
          type="button"
          onClick={onCopy}
          aria-label={copied ? t("copied") : t("copy")}
          variant="ghost"
          size="sm"
          className="pointer-events-auto size-[44px] rounded-md px-0 hover:bg-transparent active:translate-y-0 active:bg-transparent"
        >
          {copied ? (
            <Check className="size-[18px]" aria-hidden="true" />
          ) : (
            <Clipboard className="size-[18px]" aria-hidden="true" />
          )}
        </Button>
      </div>
    </article>
  );
}

function raceSurfaceClassName(surface: PlannerRace["surface"]) {
  if (surface === "DIRT") {
    return "bg-warning text-deep-ink-text";
  }
  if (surface === "TURF") {
    return "bg-success text-deep-ink-text";
  }
  return "bg-deep-ink text-deep-ink-text";
}

function RaceDetails({
  race,
  locale,
}: {
  readonly race: PlannerRace;
  readonly locale: PlannerLocale;
}) {
  const t = useTranslations("Planner");
  const handicap =
    race.handicapLow === null
      ? null
      : race.handicapHigh === null
        ? `${race.handicapLow}+`
        : `${race.handicapLow}–${race.handicapHigh}`;
  const instant = (value: string | null) =>
    value === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(value));
  const yesNo = (value: boolean | null) =>
    value === null ? null : value ? t("yes") : t("no");

  return (
    <div className="col-start-1 row-start-2 mt-2 divide-y divide-border-subtle border-t border-border-subtle text-[12px] leading-4">
      <RaceDetailGroup
        title={t("raceConditions")}
        items={[
          { label: t("handicapRating"), value: handicap },
          {
            label: t("benchmarkRating"),
            value: race.benchmarkRating?.toLocaleString(locale) ?? null,
          },
          {
            label: t("ratedMinimum"),
            value: race.ratedMinimum?.toLocaleString(locale) ?? null,
          },
          { label: t("ageRestriction"), value: race.ageRestriction },
          {
            label: t("minimumAge"),
            value: race.minAge?.toLocaleString(locale) ?? null,
          },
          { label: t("sexRestriction"), value: race.sexRestriction },
          { label: t("uaeBredOnly"), value: yesNo(race.uaeBredOnly) },
          {
            label: t("privateOwnersOnly"),
            value: yesNo(race.privateOwnersOnly),
          },
          {
            label: t("invitationOnly"),
            value: yesNo(race.invitationOnly),
          },
        ]}
      />
      <RaceDetailGroup
        title={t("feesAndWeights")}
        items={[
          { label: t("entryFee"), value: race.entryFee },
          { label: t("declarationFee"), value: race.declarationFee },
          { label: t("baseWeight"), value: race.baseWeight, wide: true },
          { label: t("penalties"), value: race.penalties, wide: true },
          { label: t("allowances"), value: race.allowances, wide: true },
        ]}
      />
      <RaceDetailGroup
        title={t("deadlines")}
        items={[
          {
            label: t("ratingQualificationPublished"),
            value: race.ratingQualificationPublished,
          },
          { label: t("weightsPublished"), value: race.weightsPublished },
          { label: t("entriesClose"), value: instant(race.entriesClose) },
          {
            label: t("declarationsClose"),
            value: instant(race.declarationsClose),
          },
          {
            label: t("jockeyDeclarationsClose"),
            value: instant(race.jockeyDeclarationsClose),
          },
        ]}
      />
      <RaceDetailGroup
        title={t("notesAndEligibility")}
        items={[
          { label: t("eligibility"), value: race.eligibility, wide: true },
          { label: t("notes"), value: race.notes, wide: true },
        ]}
      />
    </div>
  );
}

type RaceDetailItem = {
  readonly label: string;
  readonly value: string | null;
  readonly wide?: boolean;
};

function RaceDetailGroup({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly RaceDetailItem[];
}) {
  const visibleItems = items.filter(
    ({ value }) => value !== null && value !== "",
  );
  if (visibleItems.length === 0) return null;
  return (
    <section className="py-2">
      <h3 className="text-xs leading-4 font-bold text-secondary">{title}</h3>
      <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
        {visibleItems.map((item) => (
          <RaceDetail key={item.label} {...item} />
        ))}
      </dl>
    </section>
  );
}

function RaceDetail({ label, value, wide = false }: RaceDetailItem) {
  if (value === null || value === "") return null;
  return (
    <div className={wide ? "col-span-full min-w-0" : "min-w-0"}>
      <dt className="inline font-semibold text-secondary after:content-[':']">
        {label}
      </dt>{" "}
      <dd
        className="inline whitespace-pre-wrap text-primary [overflow-wrap:anywhere]"
        dir="auto"
      >
        {value}
      </dd>
    </div>
  );
}
